import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { SortIcon } from '@/components/common/SortIcon';
import { DeleteDialog } from '@/components/common/Dialogs';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { hourlyPackagesQueryKeys } from '../api';
import { HourlyPackageAction } from './HourlyPackageAction';
import { HourlyPackageStatusToggle } from './ChangeHourlyPackageStatusButton';
import type { HourlyPackage, PackageFilters } from '../types';

interface GetColumnsParams {
  orderBy: string;
  order: 'asc' | 'desc' | '';
  onSort: (field: string) => void;
  filters: PackageFilters;
}

export function useHourlyPackageColumns({
  orderBy,
  order,
  onSort,
}: GetColumnsParams): ColumnDef<HourlyPackage>[] {
  const { t } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);

  const columns: ColumnDef<HourlyPackage>[] = [
    {
      accessorKey: 'name',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('name')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('name')}
        >
          <p>{t('name')}</p>
          <SortIcon active={orderBy === 'name'} direction={order} />
        </div>
      ),
      cell: ({ row }) => (
        <Link
          to={`/app/hourly-packages/${row.original.id}`}
          className="text-blue-700 hover:underline"
        >
          {row.original.name}
        </Link>
      ),
    },
    {
      accessorKey: 'hours',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('hours')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('hours')}
        >
          <p>{t('hours')}</p>
          <SortIcon active={orderBy === 'hours'} direction={order} />
        </div>
      ),
      cell: ({ row }) => <div>{row.original.hours}</div>,
    },
    {
      accessorKey: 'price',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('price')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('price')}
        >
          <p>{t('price')}</p>
          <SortIcon active={orderBy === 'price'} direction={order} />
        </div>
      ),
      cell: ({ row }) => <div>{row.original.price?.toLocaleString()}</div>,
    },
    {
      accessorKey: 'overTimeHourlyCost',
      header: () => (
        <div className="flex items-center">
          <p>{t('overTimeHourlyCost')}</p>
        </div>
      ),
      cell: ({ row }) => <div>{row.original.overTimeHourlyCost?.toLocaleString()}</div>,
    },
    {
      accessorKey: 'rates',
      header: () => (
        <div className="flex items-center">
          <p>{t('rates')}</p>
        </div>
      ),
      cell: ({ row }) => <div>{row.original.rates?.length ?? 0}</div>,
    },
    {
      accessorKey: 'status',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('status')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('status')}
        >
          <p>{t('status.status')}</p>
          <SortIcon active={orderBy === 'status'} direction={order} />
        </div>
      ),
      cell: ({ row }) => {
        const { id, status } = row.original;
        return (
          <HourlyPackageStatusToggle id={id} currentStatus={status} />
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('createdAt')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('createdAt')}
        >
          <p>{t('createdAt')}</p>
          <SortIcon active={orderBy === 'createdAt'} direction={order} />
        </div>
      ),
      cell: ({ row }) => {
        const createdAt = row.original.createdAt;
        return <div>{format(new Date(createdAt), 'yyyy-MM-dd HH:mm')}</div>;
      },
    },
  ];

  if (hasAccess('hourly-package', 'update') || hasAccess('hourly-package', 'delete')) {
    columns.splice(0, 0, {
      id: 'actions',
      header: t('actionsLabel'),
      cell: ({ row }) => {
        const data = row.original;
        return (
          <div className="flex gap-2">
            {hasAccess('hourly-package', 'update') && (
              <HourlyPackageAction id={data.id} />
            )}
            {hasAccess('hourly-package', 'delete') && (
              <DeleteDialog
                id={data.id}
                title={data.name}
                url="hourly-packages"
                queryKey={hourlyPackagesQueryKeys.lists()}
              />
            )}
          </div>
        );
      },
    });
  }

  return columns;
}
